import { getAccessToken } from "@privy-io/react-auth";
import * as Sentry from "@sentry/react";
import axios from "axios";

import { config_domain, config_apiEndpoint } from "src/config";

export const SERVICE_1 = "service1";
export const SERVICE_2 = "service2";
export const SERVICE_ADMIN = "admin";
export const SERVICE_ADMIN_2 = "admin2";

const ACCESS_TOKEN_TIMEOUT_MS = 10000;

const getAccessTokenWithTimeout = () => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error("getAccessToken timed out after 10s")),
      ACCESS_TOKEN_TIMEOUT_MS
    );
  });

  return Promise.race([getAccessToken(), timeoutPromise])
    .finally(() => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    });
};

export const callLambda = async ({ method, service, url, body = {}, queryStringParameters = {}, unauthed = false, bustCache = true }) => {
  let accessToken;
  if (!unauthed) {
    try {
      accessToken = await getAccessTokenWithTimeout();
    }
    catch (err) {
      Sentry.captureException(err, {
        tags  : { component: "callLambda", endpoint: url },
        extra : { service, method },
        level : "error",
      });
      throw err;
    }
    if (!accessToken) {
      const err = new Error(`No access token available for authenticated call to ${service}/${url}`);
      Sentry.captureException(err, {
        tags  : { component: "callLambda", endpoint: url },
        extra : { service, method },
        level : "warning",
      });
      // Intentionally returning null (not throwing) to preserve existing caller behavior.
      // SWR callers currently handle null as "no data yet" rather than an error, preventing
      // the error boundary from triggering. Fixing the resulting infinite spinner is tracked
      // as a follow-up to QA-445.
      return null;
    }
  }
  const headers = unauthed ? {} : { Authorization: `Bearer ${accessToken}` };
  let slug = "/";
  if (window.location.pathname.match(/\//g).length > 1) {
    // if 2 slashes or more are found in the path, then it means that a slug is present
    slug = window.location.pathname.split("/")[2];
  }
  const { data } = await axios({
    url    : `${config_apiEndpoint}/${service}/${url}`,
    method,
    headers,
    params : {
      ...queryStringParameters,
      domain: config_domain,
      slug,
      ...(bustCache && { t: Date.now() })
    },
    data: body,
  });
  return data;
};

export const fetchAllResult = async ({ service, url }) => {
  const result = [];
  let lastItemKey;

  do {
    const response = await callLambda({
      method : "POST",
      service,
      url,
      body   : {
        pageLimit         : 10000,
        ExclusiveStartKey : lastItemKey
      }
    });

    result.push(...response.Items);
    lastItemKey = response.LastEvaluatedKey;
  } while (lastItemKey);

  return result;
};

export const addNewPrivateAsset = async (portalId, type, data, name, contentType = "image/png") => {
  const { signedUrl, id } = await callLambda({
    method                : "post",
    service               : SERVICE_1,
    url                   : "addNewPrivateAsset",
    body                  : { type, name, contentType },
    queryStringParameters : { portalId }
  });
  await axios({
    data,
    method  : "PUT",
    url     : signedUrl,
    headers : { "Content-Type": contentType }
  });
  return id;
};

export const uploadFileToS3BySlug = async (slug, data, name, contentType = "application/pdf") => {
  const { signedUrl, id } = await callLambda({
    method   : "post",
    service  : SERVICE_1,
    url      : "getUserPreSignedUrlBySlug",
    body     : { contentType, name, slug },
    unauthed : true
  });

  await axios({
    data,
    method  : "PUT",
    url     : signedUrl,
    headers : { "Content-Type": contentType }
  });

  return id;
};
